'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Bot, Rocket, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export function OnboardingGreeting() {
    const [isVisible, setIsVisible] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        // Check if the user has already explored
        const hasExplored = localStorage.getItem('upbeat_onboarding_done');
        if (!hasExplored) {
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    const dismiss = () => {
        setIsVisible(false);
        localStorage.setItem('upbeat_onboarding_done', 'true');
    };

    const handleExplore = () => {
        if (typeof window !== 'undefined') {
            // Check if mobile, open sidebar if so
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
                const trigger = document.getElementById('mobile-menu-trigger');
                trigger?.click();
            }
            
            // Dispatch the tour start
            window.dispatchEvent(new CustomEvent('start-site-tour'));
            dismiss();
        }
    };

    if (!mounted || !isVisible) return null;

    return (
        <div className={cn(
            "fixed bottom-24 left-4 z-[60] w-[calc(100%-2rem)] max-w-sm transition-all duration-700 ease-in-out transform",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0 pointer-events-none"
        )}>
            <Card className="shadow-2xl border-2 border-primary/20 bg-background/95 backdrop-blur-lg overflow-hidden ring-4 ring-primary/5">
                <CardHeader className="relative pb-2 bg-gradient-to-br from-primary/10 to-transparent">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="absolute right-2 top-2 h-8 w-8 rounded-full hover:bg-background/50 transition-colors" 
                        onClick={dismiss}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="bg-primary p-2 rounded-xl shadow-lg shadow-primary/20">
                            <Bot className="h-6 w-6 text-white animate-pulse" />
                        </div>
                        <CardTitle className="text-xl font-headline font-bold">Welcome Home!</CardTitle>
                    </div>
                    <CardDescription className="text-sm font-semibold text-foreground">
                        I'm UpBot. Let's get your child started on a path to success.
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-4">
                    <Button 
                        onClick={handleExplore}
                        size="lg"
                        variant="accent"
                        className="w-full rounded-xl py-8 text-lg font-bold shadow-xl shadow-accent/20 hover:scale-105 transition-transform group"
                    >
                        <Rocket className="mr-2 h-6 w-6 group-hover:animate-bounce" />
                        Explore Our World
                        <ArrowRight className="ml-auto h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </Button>
                    
                    <div className="mt-4 text-center">
                        <button 
                            className="text-xs text-muted-foreground hover:text-primary transition-colors font-medium underline-offset-4 hover:underline"
                            onClick={dismiss}
                        >
                            I'll look around myself
                        </button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
